import { Grid, styled, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  IJobBoardDTO,
  jobBoardSearchAccountUsersType,
  updateJobBoardAccounts,
  regenerateDiceToken,
} from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  ControlledTextInput,
  WsButton,
  Label,
  WsDrawer,
} from 'wisestep-base';

import {
  useNativeSearchAccessControl,
  NativeSearchAction,
} from '../../../../hooks/useNativeSearchAccessControl';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import SearchSharedUser from '../searchSharedUser/SearchSharedUser';
import { useNativeSearchQueryStore } from '../store/NativeSearchQueryStore';

import SharedUserList from './SharedUserList';
import ShareQuota from './ShareQuota';

type PropTypes = {
  accountType:
    | 'DICE_SEARCH'
    | 'CAREER_BUILDER'
    | 'CAREER_BUILDER_EDGE'
    | 'TECH_FETCH'
    | 'CV_LIBRARY'
    | 'MONSTER'
    | 'JOB_SERVE'
    | 'CW_JOBS'
    | 'JOB_SITE';
  tableData: IJobBoardDTO;
  dataIndex: number;
  dataUpdater(data: IJobBoardDTO, index: number);
  openDrawer: boolean;
  closeDrawer(): void;
  country: string;
};
const BoldLabel = styled(Label)(() => ({
  fontWeight: 'bold',
}));
const AccountSettings = ({
  accountType,
  tableData,
  dataIndex,
  dataUpdater,
  openDrawer,
  closeDrawer,
  country,
}: PropTypes) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const searchEmail = useNativeSearchQueryStore((state) => state.email);

  const [email, setEmail] = useState<string>(searchEmail);
  const { access, owns } = useNativeSearchAccessControl([
    NativeSearchAction.MANAGE_NATIVE_SEARCH_ACCOUNTS,
  ]);
  const [manage] = access;
  const drawerTitle = 'Account Settings';
  const [singleAccData, setSingleAccData] = useState<IJobBoardDTO>(tableData);

  const methods = useForm();
  const { handleSubmit } = methods;

  useEffect(() => {
    if (email) {
      setSingleAccData({
        ...tableData,
        jobBoardSearchAccountUsers: email
          ? tableData.jobBoardSearchAccountUsers.filter((user) => user.email == email)
          : tableData.jobBoardSearchAccountUsers,
      });
    } else {
      setSingleAccData(tableData);
    }
  }, [country, tableData, email]);
  const onRegenerateToken = async () => {
    try {
      await regenerateDiceToken(singleAccData?.id);
      setNotification(success('Regenerated Successfully'));
    } catch (error) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };

  const onEditHandler = (
    data: jobBoardSearchAccountUsersType[],
    userData: jobBoardSearchAccountUsersType,
    isDelete: boolean
  ) => {
    if (isDelete) {
      tableData.jobBoardSearchAccountUsers = tableData.jobBoardSearchAccountUsers.filter(
        (user) => user.email !== userData.email
      );
    } else {
      tableData.jobBoardSearchAccountUsers.map((user) => {
        if (user.email === userData.email) {
          user.maxQueries = userData.maxQueries;
        }
      });
    }
    const tempData = singleAccData;
    const otherCountryAccount = data?.filter((obj) => {
      if (!isEmpty(obj.country)) return obj.country?.toUpperCase() !== country.toUpperCase();
      else return data;
    });
    tempData.jobBoardSearchAccountUsers = otherCountryAccount
      ? data
      : [...otherCountryAccount, ...data];
    setSingleAccData({ ...tempData });
  };

  const filterSharedAccount = (data: jobBoardSearchAccountUsersType[]) => {
    if (data === null) return [];

    const filtered = data?.filter((obj) => {
      if (!isEmpty(obj.country)) return obj.country?.toUpperCase() === country.toUpperCase();
      else return data;
    });

    return filtered;
  };

  const apiCallHandler = async (data: FieldValues) => {
    const payload: IJobBoardDTO = {
      id: singleAccData.id,
      jobBoardName: singleAccData.jobBoardName,
      jobBoardSearchAccountTeams: [],
      jobBoardSearchAccountUsers: tableData.jobBoardSearchAccountUsers,
      username: data?.userName ? data?.userName : singleAccData.username,
      password: data?.password ? data?.password : '',
    };
    try {
      await updateJobBoardAccounts(payload);
      setNotification(success('Updated Successfully'));
      closeDrawer();
    } catch (error) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };
  return (
    <FormProvider {...methods}>
      <WsDrawer
        open={openDrawer}
        size="large"
        onClose={() => closeDrawer()}
        iconType="cross"
        title={drawerTitle}
        headerSize="small"
      >
        <Box p={3}>
          <Grid container pt={1}>
            {accountType === 'DICE_SEARCH' && (
              <Label>
                Re-Generate Tokens |
                {
                  <WsButton onClick={() => onRegenerateToken()} variant="text">
                    Click Here to re-generate tokens
                  </WsButton>
                }
              </Label>
            )}
            {accountType !== 'MONSTER' && (
              <>
                <Grid item xs={12} pt={0.5}>
                  <ControlledTextInput
                    name="userName"
                    labelText="Username:"
                    type="text"
                    fullWidth
                    id="userName"
                    placeholder="Username"
                    variant="outlined"
                    required
                    value={singleAccData.username}
                  />
                  <ControlledTextInput
                    name="password"
                    labelText="Password:"
                    type="text"
                    fullWidth
                    id="password"
                    placeholder="Password"
                    variant="outlined"
                    required
                  />
                </Grid>
              </>
            )}
            <Grid container direction="row" justifyContent="space-between" item xs={12} mt={2}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                Per Email Quota for {country}
              </WsTypography>
              {(owns || manage) && (
                <Stack direction="row" spacing={2}>
                  <WsButton onClick={handleSubmit(apiCallHandler)}>Save</WsButton>
                  <WsButton onClick={() => closeDrawer()}>Cancel</WsButton>
                </Stack>
              )}
            </Grid>
            <SearchSharedUser isListing={false} searchSharedAccount={setEmail} />
            <Grid item xs={12} mt={2}>
              {(owns || manage) && (
                <ShareQuota
                  tableData={singleAccData}
                  dataIndex={dataIndex}
                  country={country}
                  onClose={() => closeDrawer()}
                  dataUpdater={(data, index) => dataUpdater(data, index)}
                />
              )}
            </Grid>
            <Grid item xs={5}>
              <BoldLabel>Shared Workspace User</BoldLabel>
            </Grid>
            <Grid item xs={4}>
              <BoldLabel>Views/Day</BoldLabel>
            </Grid>
            <SharedUserList
              sharedUsersData={
                !isEmpty(singleAccData?.jobBoardSearchAccountUsers)
                  ? filterSharedAccount(singleAccData?.jobBoardSearchAccountUsers)
                  : []
              }
              onSaveHandler={(data, userData) => onEditHandler(data, userData, false)}
              onDeleteHandler={(data, userData) => onEditHandler(data, userData, true)}
            />
          </Grid>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AccountSettings;
